import React, { useMemo } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { OpportunityEstimatedFinancialsChartProps } from '../../../lib/types'
import { MD } from '../../../lib/utils/constants'
import { useBreakpoint } from '../../../lib/utils/hooks'
import {
  EstimatedFinancialGraphLayerConfig,
  formatEstimatedFinancialAxisValue,
  formatEstimatedFinancialTooltipValue,
  formatEstimatedFinancialXAxisLabel,
  getEstimatedFinancialYAxisScale,
  getMetricGraphLayerConfig,
} from './utils'

type EstimatedFinancialsXAxisTickProps = {
  x?: number
  y?: number
  payload?: { value?: string | number }
  index?: number
  visibleTicksCount?: number
  isCompact: boolean
  maxLabelLength: number
}

const EstimatedFinancialsXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  index = 0,
  visibleTicksCount = 0,
  isCompact,
  maxLabelLength,
}: EstimatedFinancialsXAxisTickProps) => {
  const isFirst = index === 0
  const isLast = visibleTicksCount > 0 && index === visibleTicksCount - 1
  const textAnchor = isCompact || isLast ? 'end' : isFirst ? 'start' : 'middle'

  return (
    <text
      x={x}
      y={y}
      dy={isCompact ? 4 : 12}
      textAnchor={textAnchor}
      fill='#63768b'
      fontSize={isCompact ? 10 : 12}
      transform={isCompact ? `rotate(-35, ${x}, ${y})` : undefined}
    >
      {formatEstimatedFinancialXAxisLabel(String(payload?.value ?? ''), maxLabelLength)}
    </text>
  )
}

const OpportunityEstimatedFinancialsChart = ({
  graphType,
  data,
  metricName,
  currencyCode,
  barColor,
  label,
  comparisonSeries,
}: OpportunityEstimatedFinancialsChartProps) => {
  const { breakpoint } = useBreakpoint()
  const isCompact = (breakpoint ?? Number.MAX_SAFE_INTEGER) <= MD
  const layers = getMetricGraphLayerConfig(graphType)
  const effectiveLayers = useMemo((): EstimatedFinancialGraphLayerConfig => {
    if (comparisonSeries) {
      return layers
    }

    switch (graphType) {
      case 'bar':
      case 'bar+bar':
      case 'bar+line':
        return { bars: 1, lines: 0 }
      case 'line':
      case 'line+line':
      default:
        return { bars: 0, lines: 1 }
    }
  }, [comparisonSeries, graphType, layers])

  const secondaryColor = comparisonSeries?.barColor ?? barColor
  const secondaryLabel = comparisonSeries?.label ?? `${label} (Compare)`
  const gradientId = useMemo(
    () => `estimatedFinancials-${metricName.replace(/[^a-zA-Z0-9]/g, '-')}`,
    [metricName],
  )

  const { max: yAxisMax, ticks: yAxisTicks } = useMemo(
    () => getEstimatedFinancialYAxisScale(data),
    [data],
  )
  const yAxisWidth = useMemo(() => {
    const labels = yAxisTicks.map((tick) =>
      formatEstimatedFinancialAxisValue(tick, metricName, currencyCode),
    )
    const longestLabel = labels.reduce(
      (longest, current) => (current.length > longest.length ? current : longest),
      '',
    )
    const calculatedWidth = Math.ceil(longestLabel.length * (isCompact ? 6.5 : 7.5) + 16)
    return isCompact ? Math.min(calculatedWidth, 64) : Math.max(72, calculatedWidth)
  }, [currencyCode, isCompact, metricName, yAxisTicks])

  const chartHeight = isCompact ? 240 : 280
  const xAxisMaxLabelLength = isCompact ? 10 : 16

  if (!data.length) {
    return (
      <p
        className='opportunity-estimated-financials-empty'
        data-testid='estimated-financials-empty'
      >
        Financial estimates are not available yet.
      </p>
    )
  }

  const formatTooltipValue = (value: number, name: string) => {
    if (name === label) {
      return [formatEstimatedFinancialTooltipValue(value, metricName, currencyCode), name]
    }

    if (comparisonSeries && name === secondaryLabel) {
      return [
        formatEstimatedFinancialTooltipValue(
          value,
          comparisonSeries.metricName,
          comparisonSeries.currencyCode,
        ),
        name,
      ]
    }

    return [value, name]
  }

  return (
    <section
      className='opportunity-estimated-financials-chart-wrap'
      data-testid='estimated-financials-chart'
      data-graph-type={graphType}
    >
      <div className='opportunity-estimated-financials-chart-canvas'>
        <ResponsiveContainer width='100%' height={chartHeight}>
          <ComposedChart
            data={data}
            margin={{
              left: 0,
              right: isCompact ? 16 : 28,
              top: 8,
              bottom: isCompact ? 2 : 4,
            }}
          >
            <defs>
              <linearGradient id={`${gradientId}-primary-bar`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor={barColor} stopOpacity={0.95} />
                <stop offset='95%' stopColor={barColor} stopOpacity={0.45} />
              </linearGradient>
              <linearGradient id={`${gradientId}-secondary-bar`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor={secondaryColor} stopOpacity={0.95} />
                <stop offset='95%' stopColor={secondaryColor} stopOpacity={0.35} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray='3 3'
              vertical={false}
              stroke='rgba(226, 226, 226, 0.9)'
            />
            <XAxis
              dataKey='year'
              tick={(props) => (
                <EstimatedFinancialsXAxisTick
                  {...props}
                  isCompact={isCompact}
                  maxLabelLength={xAxisMaxLabelLength}
                />
              )}
              tickLine={false}
              axisLine={false}
              tickMargin={isCompact ? 8 : 6}
              minTickGap={isCompact ? 4 : 16}
              interval='preserveStartEnd'
              height={isCompact ? 56 : 32}
              padding={{ left: isCompact ? 8 : 12, right: isCompact ? 12 : 20 }}
            />
            <YAxis
              orientation='left'
              width={yAxisWidth}
              ticks={yAxisTicks}
              domain={[0, yAxisMax]}
              tick={{ fontSize: isCompact ? 10 : 12, fill: '#63768b' }}
              tickLine={false}
              axisLine={false}
              tickMargin={isCompact ? 4 : 8}
              tickFormatter={(value) =>
                formatEstimatedFinancialAxisValue(Number(value), metricName, currencyCode)
              }
            />
            <Tooltip
              wrapperClassName='opportunity-estimated-financials-tooltip-wrap'
              cursor={{ fill: 'rgba(13, 134, 255, 0.08)' }}
              formatter={formatTooltipValue}
            />
            {effectiveLayers.bars >= 1 ? (
              <Bar
                dataKey='value'
                name={label}
                fill={`url(#${gradientId}-primary-bar)`}
                stroke={barColor}
                strokeWidth={1}
                radius={[8, 8, 0, 0]}
                maxBarSize={isCompact ? 36 : 48}
                isAnimationActive={false}
              />
            ) : null}
            {effectiveLayers.bars === 2 ? (
              <Bar
                dataKey='secondaryValue'
                name={secondaryLabel}
                fill={`url(#${gradientId}-secondary-bar)`}
                stroke={secondaryColor}
                strokeWidth={1}
                radius={[8, 8, 0, 0]}
                maxBarSize={isCompact ? 28 : 36}
                isAnimationActive={false}
              />
            ) : null}
            {effectiveLayers.lines >= 1 ? (
              <Line
                type='monotone'
                dataKey={graphType === 'bar+line' ? 'secondaryValue' : 'value'}
                name={graphType === 'bar+line' ? secondaryLabel : label}
                stroke={graphType === 'bar+line' ? secondaryColor : barColor}
                strokeWidth={2}
                dot={{
                  r: isCompact ? 3 : 4,
                  fill: graphType === 'bar+line' ? secondaryColor : barColor,
                  strokeWidth: 0,
                }}
                activeDot={{ r: isCompact ? 5 : 6 }}
                isAnimationActive={false}
              />
            ) : null}
            {effectiveLayers.lines === 2 ? (
              <Line
                type='monotone'
                dataKey='secondaryValue'
                name={secondaryLabel}
                stroke={secondaryColor}
                strokeWidth={2}
                strokeDasharray='4 4'
                dot={{ r: isCompact ? 3 : 4, fill: secondaryColor, strokeWidth: 0 }}
                activeDot={{ r: isCompact ? 5 : 6 }}
                isAnimationActive={false}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {comparisonSeries ? (
        <ul
          className='opportunity-estimated-financials-chart-legend'
          data-testid='estimated-financials-chart-legend'
        >
          <li>
            <span
              className='opportunity-estimated-financials-chart-legend-swatch'
              style={{ backgroundColor: barColor }}
              aria-hidden='true'
            />
            <span>{label}</span>
          </li>
          <li>
            <span
              className='opportunity-estimated-financials-chart-legend-swatch'
              style={{ backgroundColor: secondaryColor }}
              aria-hidden='true'
            />
            <span>{secondaryLabel}</span>
          </li>
        </ul>
      ) : null}
    </section>
  )
}

export default OpportunityEstimatedFinancialsChart
